import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL}/truck-tires`;

export interface TruckTire {
  _id: string;
  UrunID: number;
  MalzemeKodu: string;
  MalzemeAdi: string;
  MalzemeAdim: string;
  Dod?: string;
  Not?: string;
  Kategori: string;
  Markasi: string;
  AracGrubu: string;
  Grup: string;
  Birim: string;
  UcretsizKargo?: string;
  Kdv: number;
  ListeFiyati: number;
  HavaleIndirimi: number;
  TekCekimIndirimi: number;
  TaksitIndirimi: number;
  HavaleFiyati: number;
  TekCekimFiyati: number;
  TaksitFiyati: number;
  SonAlisFiyati: number;
  StokMiktari: number;
  UrunResim?: string;
  UrunTipi: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TruckTireCategory {
  kategori: string;
  urunSayisi: number;
  markalar: string[];
}

export interface TruckTireBrand {
  marka: string;
  urunSayisi: number;
  kategoriler: string[];
}

export interface TruckTirePagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface TruckTireFilters {
  page?: number;
  limit?: number;
  grup?: string | string[];
  markasi?: string | string[];
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TruckTireResponse {
  success: boolean;
  data: {
    truckTires: TruckTire[];
    pagination: TruckTirePagination;
  };
  message?: string;
}

export interface TruckTireSingleResponse {
  success: boolean;
  data: TruckTire;
  message?: string;
}

export interface TruckTireCategoriesResponse {
  success: boolean;
  data: TruckTireCategory[];
  message?: string;
}

export interface TruckTireBrandsResponse {
  success: boolean;
  data: TruckTireBrand[];
  message?: string;
}

class TruckTireService {
  // Tüm kamyon lastiklerini getir
  async getAllTruckTires(filters: TruckTireFilters = {}): Promise<TruckTireResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Array parametrelerini (grup, markasi) her biri için ayrı ayrı ekle
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await axios.get(`${API_URL}?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastikleri getirilemedi');
    }
  }

  // Kategoriye göre kamyon lastikleri getir
  async getTruckTiresByCategory(grup: string, filters: Omit<TruckTireFilters, 'grup'> = {}): Promise<TruckTireResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.get(`${API_URL}/category/${encodeURIComponent(grup)}?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kategori kamyon lastikleri getirilemedi');
    }
  }

  // Tek kamyon lastiği getir
  async getTruckTireById(id: string | number): Promise<TruckTireSingleResponse> {
    try {
      const response = await axios.get(`${API_URL}/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastiği bulunamadı');
    }
  }

  // Kamyon lastik kategorilerini getir
  async getTruckTireCategories(): Promise<TruckTireCategoriesResponse> {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastik kategorileri getirilemedi');
    }
  }

  // Kamyon lastik markalarını getir
  async getTruckTireBrands(): Promise<TruckTireBrandsResponse> {
    try {
      const response = await axios.get(`${API_URL}/brands`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastik markaları getirilemedi');
    }
  }

  // Admin: Kamyon lastiği oluştur
  async createTruckTire(truckTireData: Omit<TruckTire, '_id' | 'createdAt' | 'updatedAt'>): Promise<TruckTireSingleResponse> {
    try {
      const response = await axios.post(API_URL, truckTireData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastiği oluşturulamadı');
    }
  }

  // Admin: Kamyon lastiği güncelle
  async updateTruckTire(id: string | number, truckTireData: Partial<TruckTire>): Promise<TruckTireSingleResponse> {
    try {
      const response = await axios.put(`${API_URL}/${id}`, truckTireData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastiği güncellenemedi');
    }
  }

  // Admin: Kamyon lastiği sil
  async deleteTruckTire(id: string | number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.delete(`${API_URL}/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Kamyon lastiği silinemedi');
    }
  }

  // Resim URL'sini formatla
  formatImageUrl(imageName?: string, truckTire?: TruckTire): string {
    if (!imageName) return '/images/placeholder-tire.jpg';
    
    // Eğer tam URL ise olduğu gibi döndür
    if (imageName.startsWith('http')) return imageName;
    
    // Kamyon lastikleri için özel resim havuzunu kullan
    if (truckTire) {
      // UrunID'ye göre rastgele ama tutarlı bir resim seç
      const imageIndex = ((truckTire.UrunID || 1) % 43) + 1; // 1-43 arası
      return `/uploads/images/truck-tires/truck_${imageIndex}.webp`;
    }
    
    // uploads klasöründen göster
    return `/uploads/products/${imageName}`;
  }

  // Fiyat formatla
  formatPrice(price: number): string {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  // İndirim yüzdesini hesapla
  calculateDiscountPercentage(listPrice: number, discountedPrice: number): number {
    if (listPrice <= 0 || discountedPrice <= 0) return 0;
    return Math.round(((listPrice - discountedPrice) / listPrice) * 100);
  }
}

const truckTireService = new TruckTireService();
export default truckTireService;
